import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Stack,
  Box,
  IconButton,
  CircularProgress,
  Switch,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Typography,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import { PageHeader, SubtitleM } from "../../components/Typography";
import SearchBar from "../../components/SearchBar";
import SideBar from "../../components/SideBar";
import SectionTopBar from "../../components/SectionTopBar";
import useApiCall from "../../hooks/useApiCall";

export default function PromptVariableTemplatesList() {
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [formData, setFormData] = useState({
    variable_name: "",
    variable_type: "static",
    data_type: "string",
    category: "",
    description: "",
    is_active: true,
    value_schema: {},
    metadata: {},
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Fetch templates on mount and when search changes
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = searchQuery ? `?search=${searchQuery}` : '';
      const response = await makeRequest(`/v2/prompt_variable_templates${params}`, {
        method: 'GET',
        authTokens: tokens,
      });
      setTemplates(response.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const resetForm = () => {
    setFormData({
      variable_name: "",
      variable_type: "static",
      data_type: "string",
      category: "",
      description: "",
      is_active: true,
      value_schema: {},
      metadata: {},
    });
    setValidationErrors({});
    setError(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setEditingTemplate(null);
    setDrawerOpen(true);
  };

  const handleEdit = async (template) => {
    setLoading(true);
    try {
      const response = await makeRequest(`/v2/prompt_variable_templates/${template.id}`, {
        method: 'GET',
        authTokens: tokens,
      });
      setFormData(response.template);
      setEditingTemplate(template);
      setDrawerOpen(true);
      setError(null);
    } catch (error) {
      console.error("Error fetching template:", error);
      alert("Failed to load template. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    resetForm();
    setEditingTemplate(null);
  };

  const handleDelete = async (template) => {
    if (template.usage_count > 0) {
      alert(
        `Cannot delete template with ${template.usage_count} agent(s) using it. Please remove from agents first.`
      );
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete the variable "${template.variable_name}"?`
      )
    ) {
      try {
        await makeRequest(`/v2/prompt_variable_templates/${template.id}`, {
          method: 'DELETE',
          authTokens: tokens,
        });
        fetchTemplates();
      } catch (error) {
        console.error("Error deleting template:", error);
        alert("Failed to delete template. Please try again.");
      }
    }
  };

  const handleToggleActive = async (template) => {
    try {
      if (template.is_active) {
        await makeRequest(`/v2/prompt_variable_templates/${template.id}/deactivate`, {
          method: 'PUT',
          authTokens: tokens,
        });
      } else {
        await makeRequest(`/v2/prompt_variable_templates/${template.id}/activate`, {
          method: 'PUT',
          authTokens: tokens,
        });
      }
      fetchTemplates();
    } catch (error) {
      console.error("Error toggling template status:", error);
      alert("Failed to update template status. Please try again.");
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.variable_name) {
      errors.variable_name = "Variable name is required";
    } else if (!validateVariableName(formData.variable_name)) {
      errors.variable_name =
        "Must start with lowercase letter and contain only lowercase letters, numbers, and underscores";
    }

    if (!formData.variable_type) {
      errors.variable_type = "Variable type is required";
    }

    if (!formData.data_type) {
      errors.data_type = "Data type is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingTemplate) {
        await makeRequest(`/v2/prompt_variable_templates/${editingTemplate.id}`, {
          method: 'PUT',
          body: { template: formData },
          authTokens: tokens,
        });
      } else {
        await makeRequest('/v2/prompt_variable_templates', {
          method: 'POST',
          body: { template: formData },
          authTokens: tokens,
        });
      }
      handleCloseDrawer();
      fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      setError(
        error.response?.data?.error ||
          error.response?.data?.details?.variable_name?.[0] ||
          "Failed to save template. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader>Prompt Variable Templates</PageHeader>
      <SectionTopBar
        style={{ justifyContent: "space-between", marginTop: "30px" }}
      >
        <SearchBar triggerOn="onChange" onSearch={handleSearch} />
        <Button
          title="+ Create New Variable"
          variant="PrimaryButtonBlue"
          onClick={handleCreateNew}
        >
          + Create New Variable
        </Button>
      </SectionTopBar>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            my: 4,
            padding: "40px",
          }}
        >
          <SubtitleM>No templates found</SubtitleM>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={handleCreateNew}
          >
            Create Your First Template
          </Button>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ margin: "10px" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Variable Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Data Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Used By</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Active</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow
                  key={template.id}
                  hover
                  sx={{
                    opacity: template.is_active ? 1 : 0.6,
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  <TableCell>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: "bold",
                      }}
                    >
                      {`{{${template.variable_name}}}`}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: "300px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {template.description || "-"}
                  </TableCell>
                  <TableCell>{template.variable_type}</TableCell>
                  <TableCell>{template.data_type}</TableCell>
                  <TableCell>
                    {template.category
                      ? template.category.replace("_", " ")
                      : "-"}
                  </TableCell>
                  <TableCell>{template.usage_count || 0} agent(s)</TableCell>
                  <TableCell>
                    <Switch
                      checked={template.is_active}
                      onChange={() => handleToggleActive(template)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(template)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(template)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Right Side Drawer for Create/Edit */}
      <SideBar
        title={`${editingTemplate ? "Edit" : "Create"} Variable Template`}
        windowWidth="60%"
        maxWidth="900px"
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
      >
        <Stack spacing={3}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              {/* Variable Name */}
              <Box>
                <TextField
                  label="Variable Name *"
                  value={formData.variable_name}
                  onChange={(e) =>
                    handleFormChange("variable_name", e.target.value.toLowerCase())
                  }
                  required
                  fullWidth
                  disabled={editingTemplate}
                  error={Boolean(validationErrors.variable_name)}
                  helperText={
                    validationErrors.variable_name ||
                    "Lowercase letters, numbers, underscores only"
                  }
                  placeholder="e.g., ai_personality_guidelines"
                  size="small"
                />
              </Box>

              {/* Preview */}
              {formData.variable_name && (
                <Box
                  sx={{
                    padding: "12px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Usage in prompts:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontFamily: "monospace", fontWeight: "bold", mt: 0.5 }}
                  >
                    {`{{${formData.variable_name}}}`}
                  </Typography>
                </Box>
              )}

              {/* Variable Type */}
              <Box>
                <TextField
                  select
                  label="Variable Type *"
                  value={formData.variable_type}
                  onChange={(e) => handleFormChange("variable_type", e.target.value)}
                  required
                  fullWidth
                  error={Boolean(validationErrors.variable_type)}
                  helperText={
                    validationErrors.variable_type ||
                    "When is this variable provided?"
                  }
                  size="small"
                >
                  {getVariableTypes().map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box>
                        <Typography variant="body2">{type.label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Data Type */}
              <Box>
                <TextField
                  select
                  label="Data Type *"
                  value={formData.data_type}
                  onChange={(e) => handleFormChange("data_type", e.target.value)}
                  required
                  fullWidth
                  error={Boolean(validationErrors.data_type)}
                  helperText={
                    validationErrors.data_type || "What type of data is stored?"
                  }
                  size="small"
                >
                  {getDataTypes().map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box>
                        <Typography variant="body2">{type.label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Category */}
              <Box>
                <TextField
                  select
                  label="Category"
                  value={formData.category}
                  onChange={(e) => handleFormChange("category", e.target.value)}
                  fullWidth
                  helperText="Helps organize variables"
                  size="small"
                >
                  <MenuItem value="">None</MenuItem>
                  {getCategories().map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Description */}
              <Box>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleFormChange("description", e.target.value)}
                  multiline
                  rows={4}
                  fullWidth
                  helperText="Explain what this variable is for"
                  placeholder="e.g., Guidelines for AI personality and tone"
                />
              </Box>

              {/* Active Status */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography>Active (shown in autocomplete)</Typography>
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleFormChange("is_active", e.target.checked)
                  }
                />
              </Box>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleCloseDrawer}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={saving && <CircularProgress size={20} />}
                >
                  {saving
                    ? "Saving..."
                    : editingTemplate
                    ? "Update"
                    : "Create"}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </SideBar>
    </>
  );
}

// Helper functions
const getVariableTypes = () => [
  {
    value: "static",
    label: "Static",
    description: "Set once at template level, same for all conversations",
  },
  {
    value: "runtime",
    label: "Runtime",
    description: "Provided per conversation (from agent deployment, business unit, etc.)",
  },
  {
    value: "dynamic",
    label: "Dynamic",
    description: "Changes during conversation (context, user info, etc.)",
  },
];

const getDataTypes = () => [
  {
    value: "string",
    label: "String",
    description: "Plain text value",
  },
  {
    value: "number",
    label: "Number",
    description: "Numeric value",
  },
  {
    value: "boolean",
    label: "Boolean",
    description: "True/False value",
  },
  {
    value: "object",
    label: "Object",
    description: "JSON object with multiple fields",
  },
  {
    value: "array",
    label: "Array",
    description: "List of values",
  },
];

const getCategories = () => [
  {
    value: "personality",
    label: "Personality",
    description: "AI tone, style, behavior",
  },
  {
    value: "business_rules",
    label: "Business Rules",
    description: "Company policies, guidelines",
  },
  {
    value: "context",
    label: "Context",
    description: "User info, session data",
  },
  {
    value: "configuration",
    label: "Configuration",
    description: "System settings, preferences",
  },
];

const validateVariableName = (name) => {
  // Must start with lowercase letter
  // Can contain lowercase letters, numbers, underscores
  const regex = /^[a-z][a-z0-9_]*$/;
  return regex.test(name);
};
